import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Truck, Plus, Trash2, ArrowLeft, Save, Printer, Download, Send, X, Check, Ban, AlertTriangle, Scale } from 'lucide-react';
import CustomerDedupHints from '../../components/CustomerDedupHints';
import QuickAddCustomerButton from '../../components/QuickAddCustomerDialog';
import { toast } from 'sonner';
import CarrierSelect from '../../components/CarrierSelect';
import DocTemplatePicker from '../../components/DocTemplatePicker';
import DocumentChainStrip from '../../components/DocumentChainStrip';
import LockedDocBanner from '../../components/LockedDocBanner';
import EvidencePhotoGallery from '../../components/EvidencePhotoGallery';
import CommercialInvoiceButton from '../../components/CommercialInvoiceButton';

const STATUS_META = {
  Draft:     { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Issued:    { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  PickedUp:  { bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  Delivered: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:    { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled:  { bg: 'bg-red-100 text-red-800 border-red-200' },
};

const newLine = () => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  description: '', piece_count: 1, weight_lbs: 0,
  freight_class: '', nmfc_code: '', hazmat: false, notes: '',
});

const BillOfLadingDetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromPs = params.get('from_ps');
  const tplId = params.get('tpl');
  const dupId = params.get('dup');
  const isNew = !id || id === 'new';
  const [bol, setBol] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [slips, setSlips] = useState([]);
  const [form, setForm] = useState({
    customer_id: '', packing_slip_id: '',
    ship_date: new Date().toISOString().slice(0, 10),
    carrier: '', carrier_scac: '', tracking_or_pro_number: '',
    trailer_number: '', driver_name: '', freight_terms: '',
    freight_cost: 0, fuel_surcharge: 0, accessorial_charges: 0, cost_notes: '',
    special_instructions: '', notes: '',
    bol_type: 'Original', ship_from_name: '', ship_from_address: '', ship_from_contact: '',
    declared_value: 0, cod_amount: 0, appointment_required: false, appointment_window: '',
    customer_po_reference: '',
    lines: [newLine()],
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [variance, setVariance] = useState(null);

  const loadVariance = useCallback(async () => {
    if (!id || isNew) return;
    try { const r = await api().get(`/order-bills-of-lading/${id}/rate-variance?window_days=90`); setVariance(r.data); }
    catch { /* non-blocking */ }
  }, [api, id, isNew]);
  useEffect(() => { loadVariance(); }, [loadVariance, bol?.freight_cost, bol?.fuel_surcharge, bol?.accessorial_charges]);

  const load = useCallback(async () => {
    try {
      const [c, ps] = await Promise.all([
        api().get('/customers'),
        api().get('/packing-slips'),
      ]);
      setCustomers(c.data || []); setSlips(ps.data || []);
      if (!isNew || dupId) {
        const r = await api().get(`/order-bills-of-lading/${isNew ? dupId : id}`);
        if (isNew) toast.success(`Copied from ${r.data.bol_number}. Review, then save the new Draft`);
        setBol(isNew ? null : r.data);
        setForm({
          customer_id: r.data.customer_id,
          packing_slip_id: isNew ? '' : (r.data.packing_slip_id || ''),
          ship_date: isNew ? new Date().toISOString().slice(0, 10) : r.data.ship_date,
          carrier: r.data.carrier || '',
          carrier_scac: r.data.carrier_scac || '',
          tracking_or_pro_number: isNew ? '' : (r.data.tracking_or_pro_number || ''),
          trailer_number: r.data.trailer_number || '',
          driver_name: r.data.driver_name || '',
          freight_terms: r.data.freight_terms || '',
          freight_cost: r.data.freight_cost || 0,
          fuel_surcharge: r.data.fuel_surcharge || 0,
          accessorial_charges: r.data.accessorial_charges || 0,
          cost_notes: r.data.cost_notes || '',
          special_instructions: r.data.special_instructions || '',
          notes: r.data.notes || '',
          bol_type: r.data.bol_type || 'Original',
          ship_from_name: r.data.ship_from_name || '',
          ship_from_address: r.data.ship_from_address || '',
          ship_from_contact: r.data.ship_from_contact || '',
          declared_value: r.data.declared_value || 0,
          cod_amount: r.data.cod_amount || 0,
          appointment_required: !!r.data.appointment_required,
          appointment_window: r.data.appointment_window || '',
          customer_po_reference: r.data.customer_po_reference || '',
          lines: (r.data.lines || []).map(l => ({ ...l })),
        });
      } else if (fromPs) {
        try {
          const r = await api().post(`/order-bills-of-lading/from-packing-slip/${fromPs}`);
          toast.success(`${r.data.bol_number} draft created from PS ${r.data.packing_slip_number_snapshot}`);
          navigate(`/orders/bol/${r.data.id}`, { replace: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Spawn failed'); navigate('/orders/packing'); }
      } else if (tplId) {
        // iter96 — pre-fill a new BoL from the ?tpl=<id> template
        try {
          const list = await api().get('/doc-templates?doc_type=bill_of_lading');
          const t = (list.data || []).find(x => x.id === tplId);
          if (t) {
            const d = t.template_data || {};
            setForm(prev => ({
              ...prev,
              customer_id: d.customer_id ?? prev.customer_id,
              carrier: d.carrier ?? prev.carrier,
              carrier_scac: d.carrier_scac ?? prev.carrier_scac,
              freight_terms: d.freight_terms ?? prev.freight_terms,
              bol_type: d.bol_type ?? prev.bol_type,
              ship_from_name: d.ship_from_name ?? prev.ship_from_name,
              ship_from_address: d.ship_from_address ?? prev.ship_from_address,
              ship_from_contact: d.ship_from_contact ?? prev.ship_from_contact,
              customer_po_reference: d.customer_po_reference ?? prev.customer_po_reference,
              declared_value: d.declared_value ?? prev.declared_value,
            }));
            toast.success(`Loaded template "${t.name}"`);
          }
        } catch { /* non-blocking */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [api, id, isNew, fromPs, tplId, dupId, navigate]);
  useEffect(() => { load(); }, [load]);

  const editable = isNew || (bol && (bol.status === 'Draft' || bol.status === 'Issued'));
  const addLine = () => setForm({ ...form, lines: [...form.lines, newLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) });

  const totalPieces = (form.lines || []).reduce((a, l) => a + Number(l.piece_count || 0), 0);
  const totalWeight = (form.lines || []).reduce((a, l) => a + Number(l.weight_lbs || 0), 0);
  const hasHaz = (form.lines || []).some(l => l.hazmat);

  const save = async () => {
    if (!form.customer_id && !((form.customer_name || '').trim())) { toast.error('Pick a customer or type a name'); return; }
    const valid = (form.lines || []).filter(l => (l.description || '').trim());
    if (!valid.length) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        packing_slip_id: form.packing_slip_id || null,
        customer_id: form.customer_id,
        ship_date: form.ship_date,
        carrier: form.carrier || null,
        carrier_scac: form.carrier_scac || null,
        tracking_or_pro_number: form.tracking_or_pro_number || null,
        trailer_number: form.trailer_number || null,
        driver_name: form.driver_name || null,
        freight_terms: form.freight_terms || null,
        freight_cost: Number(form.freight_cost || 0),
        fuel_surcharge: Number(form.fuel_surcharge || 0),
        accessorial_charges: Number(form.accessorial_charges || 0),
        cost_notes: form.cost_notes || null,
        special_instructions: form.special_instructions || null,
        notes: form.notes || null,
        bol_type: form.bol_type || 'Original',
        ship_from_name: form.ship_from_name || null,
        ship_from_address: form.ship_from_address || null,
        ship_from_contact: form.ship_from_contact || null,
        declared_value: Number(form.declared_value || 0),
        cod_amount: Number(form.cod_amount || 0),
        appointment_required: !!form.appointment_required,
        appointment_window: form.appointment_window || null,
        customer_po_reference: form.customer_po_reference || null,
        lines: valid.map(l => ({
          description: l.description,
          piece_count: Number(l.piece_count || 0),
          weight_lbs: Number(l.weight_lbs || 0),
          freight_class: l.freight_class || null,
          nmfc_code: l.nmfc_code || null,
          hazmat: !!l.hazmat,
          notes: l.notes || null,
        })),
      };
      if (isNew) {
        const r = await api().post('/order-bills-of-lading', payload);
        toast.success(`${r.data.bol_number} created as Draft`);
        navigate(`/orders/bol/${r.data.id}`, { replace: true });
      } else {
        const putPayload = { ...payload };
        delete putPayload.packing_slip_id; // link is not editable post-create
        const r = await api().put(`/order-bills-of-lading/${id}`, putPayload);
        setBol(r.data);
        toast.success(`${r.data.bol_number} saved`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(true);
    try {
      const r = await api().post(`/order-bills-of-lading/${id}/${action}`, extra);
      setBol(r.data);
      toast.success(`${r.data.bol_number} → ${r.data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); } finally { setActing(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete Draft ${bol.bol_number}? This cannot be undone.`)) return;
    try {
      await api().delete(`/order-bills-of-lading/${id}`);
      toast.success('Draft deleted'); navigate('/orders/bol');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const printBol = () => {
    const base = api().defaults.baseURL;
    const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/order-bills-of-lading/${id}/print`, { headers: { Authorization: token } })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };

  const printScaleLabel = () => {
    const base = api().defaults.baseURL;
    const token = api().defaults.headers?.Authorization;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/order-bills-of-lading/${id}/scale-ticket-label`, { headers: { Authorization: token } })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } }, 400); })
      .catch(() => { w.close(); toast.error('Scale label load failed'); });
  };

  const download = async (fmt) => {
    try {
      const res = await api().get(fmt === 'pdf' ? `/order-bills-of-lading/${id}/pdf` : `/order-bills-of-lading/${id}/export.${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : (fmt === 'json' ? 'application/json' : 'text/csv') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${bol.bol_number}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const emailBol = async () => {
    try {
      const r = await api().post(`/order-bills-of-lading/${id}/email`);
      toast.success(`Emailed to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  const statusPill = bol && (
    <Badge className={`${(STATUS_META[bol.status] || STATUS_META.Draft).bg} border rounded-sm text-xs uppercase tracking-wider`} data-testid="bol-status-pill">
      {bol.status}
    </Badge>
  );

  return (
    <div className="space-y-4" data-testid="bill-of-lading-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/bol')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="w-6 h-6 text-slate-700" />
              {isNew ? 'New Bill of Lading' : bol?.bol_number || 'Loading…'}
              {statusPill}
            </h1>
            {bol && (
              <p className="text-xs text-slate-500 mt-1">
                {bol.customer_name_snapshot}
                {bol.packing_slip_number_snapshot && <> · Linked PS: <strong>{bol.packing_slip_number_snapshot}</strong></>}
              </p>
            )}
          </div>
        </div>
        {bol && (
          <div className="flex items-center gap-1 flex-wrap">
            {bol.packing_slip_id && <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/packing/${bol.packing_slip_id}`)} className="rounded-sm text-xs" data-testid="bol-source-link">⇠ View Packing Slip {bol.packing_slip_number_snapshot}</Button>}
            <Button variant="outline" size="sm" onClick={printBol} className="rounded-sm" data-testid="bol-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={printScaleLabel} className="rounded-sm bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="bol-scale-label-btn"><Scale className="w-3.5 h-3.5 mr-1" /> Scale Label</Button>
            <Button variant="outline" size="sm" onClick={() => download('pdf')} className="rounded-sm" data-testid="bol-export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <CommercialInvoiceButton sourceType="bol" sourceId={bol.id} testId="bol-ci-btn" />
            <Button variant="outline" size="sm" onClick={() => download('json')} className="rounded-sm" data-testid="bol-export-json-btn"><Download className="w-3.5 h-3.5 mr-1" /> JSON</Button>
            <Button variant="outline" size="sm" onClick={() => download('csv')} className="rounded-sm" data-testid="bol-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={emailBol} className="rounded-sm" data-testid="bol-email-btn"><Send className="w-3.5 h-3.5 mr-1" /> Email</Button>
            {bol.status === 'Draft' && <Button size="sm" onClick={() => act('issue')} disabled={acting} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="bol-issue-btn"><Check className="w-3.5 h-3.5 mr-1" /> Issue</Button>}
            {bol.status === 'Issued' && <Button size="sm" onClick={() => act('pickup')} disabled={acting} className="bg-amber-600 hover:bg-amber-700 rounded-sm" data-testid="bol-pickup-btn"><Check className="w-3.5 h-3.5 mr-1" /> Mark Picked Up</Button>}
            {bol.status === 'PickedUp' && <Button size="sm" onClick={() => act('deliver')} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="bol-deliver-btn"><Check className="w-3.5 h-3.5 mr-1" /> Mark Delivered</Button>}
            {bol.status === 'Delivered' && <Button size="sm" onClick={() => act('close')} disabled={acting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bol-close-btn"><Check className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            {['Draft', 'Issued', 'PickedUp', 'Delivered'].includes(bol.status) && <Button size="sm" variant="outline" onClick={() => act('cancel', { reason: window.prompt('Cancel reason (optional):') || '' })} disabled={acting} className="border-red-200 text-red-700 hover:bg-red-50 rounded-sm" data-testid="bol-cancel-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Cancel</Button>}
            {bol.status === 'Draft' && <Button size="sm" variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" data-testid="bol-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        )}
      </div>

      {!isNew && <DocumentChainStrip api={api} docType="bill_of_lading" docId={id} />}
      {bol && !editable && <LockedDocBanner doc={bol} label="Bill of Lading" onDuplicate={() => navigate(`/orders/bol/new?dup=${id}`)} />}

      {editable && (
        <DocTemplatePicker
          api={api}
          docType="bill_of_lading"
          testIdPrefix="bol-tpl"
          collectCurrent={() => ({
            customer_id: form.customer_id,
            carrier: form.carrier,
            carrier_scac: form.carrier_scac,
            freight_terms: form.freight_terms,
            bol_type: form.bol_type,
            ship_from_name: form.ship_from_name,
            ship_from_address: form.ship_from_address,
            ship_from_contact: form.ship_from_contact,
            customer_po_reference: form.customer_po_reference,
            declared_value: form.declared_value,
          })}
          onApply={(d) => setForm(prev => ({
            ...prev,
            customer_id: d.customer_id ?? prev.customer_id,
            carrier: d.carrier ?? prev.carrier,
            carrier_scac: d.carrier_scac ?? prev.carrier_scac,
            freight_terms: d.freight_terms ?? prev.freight_terms,
            bol_type: d.bol_type ?? prev.bol_type,
            ship_from_name: d.ship_from_name ?? prev.ship_from_name,
            ship_from_address: d.ship_from_address ?? prev.ship_from_address,
            ship_from_contact: d.ship_from_contact ?? prev.ship_from_contact,
            customer_po_reference: d.customer_po_reference ?? prev.customer_po_reference,
            declared_value: d.declared_value ?? prev.declared_value,
          }))}
        />
      )}

      {hasHaz && (
        <div className="bg-red-50 border-2 border-red-600 text-red-900 px-3 py-2 rounded-sm text-sm font-semibold flex items-center gap-2" data-testid="bol-hazmat-banner">
          <AlertTriangle className="w-4 h-4" /> HAZARDOUS MATERIALS ON THIS SHIPMENT — Driver must have proper placards and manifest.
        </div>
      )}

      {variance && variance.variance_pct !== null && variance.variance_pct >= 20 && (
        <div className="bg-amber-50 border-2 border-amber-500 text-amber-900 px-3 py-2 rounded-sm text-sm flex items-start gap-2" data-testid="bol-variance-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="font-semibold">Rate variance detected — review this invoice before paying.</div>
            <div className="text-xs mt-0.5">
              This BoL is <strong>${variance.bol_cost_per_piece}/pc</strong> which is <strong>+{variance.variance_pct}%</strong> over
              the <strong>${variance.carrier_avg_cost_per_piece}/pc</strong> {variance.carrier} 90-day average
              (n={variance.carrier_sample_size}). Estimated overcharge: <strong>${(variance.overcharge_estimate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
            </div>
          </div>
        </div>
      )}

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Header</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="bol-customer-select"><SelectValue placeholder="Pick a customer…" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
              </div>
              <QuickAddCustomerButton api={api} disabled={!editable} testId="bol-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''))); setForm(f => ({ ...f, customer_id: c.id })); }} />
            </div>
          </div>
          <div className="col-span-2">
            <Label>Linked Packing Slip (optional)</Label>
            <Select value={form.packing_slip_id || 'none'} onValueChange={(v) => setForm({ ...form, packing_slip_id: v === 'none' ? '' : v })} disabled={!editable || !isNew}>
              <SelectTrigger className="rounded-sm" data-testid="bol-ps-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— standalone —</SelectItem>{slips.map(s => <SelectItem key={s.id} value={s.id}>{s.ps_number} — {s.customer_name_snapshot}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Ship Date</Label><Input type="date" value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="bol-ship-date" /></div>
          <div><Label>Freight Terms</Label><Input value={form.freight_terms} onChange={(e) => setForm({ ...form, freight_terms: e.target.value })} placeholder="Prepaid / Collect / 3rd Party" disabled={!editable} className="rounded-sm" data-testid="bol-freight-terms" /></div>
          <div><Label>BoL Type</Label>
            <Select value={form.bol_type || 'Original'} onValueChange={(v) => setForm({ ...form, bol_type: v })} disabled={!editable}>
              <SelectTrigger className="rounded-sm" data-testid="bol-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Original">Original</SelectItem><SelectItem value="Memorandum">Memorandum</SelectItem><SelectItem value="Short Form">Short Form</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Customer PO #</Label><Input value={form.customer_po_reference} onChange={(e) => setForm({ ...form, customer_po_reference: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="bol-customer-po" /></div>
          <div><Label>Declared Value ($)</Label><Input type="number" min="0" step="0.01" value={form.declared_value} onChange={(e) => setForm({ ...form, declared_value: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="bol-declared-value" /></div>
          <div><Label>COD Amount ($)</Label><Input type="number" min="0" step="0.01" value={form.cod_amount} onChange={(e) => setForm({ ...form, cod_amount: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="bol-cod-amount" /></div>
          <div className="flex items-center gap-2 pt-6"><Checkbox checked={!!form.appointment_required} onCheckedChange={(v) => setForm({ ...form, appointment_required: !!v })} disabled={!editable} data-testid="bol-appointment-checkbox" id="bol-appointment-checkbox" /><Label htmlFor="bol-appointment-checkbox" className="cursor-pointer text-xs">Appointment required</Label></div>
          <div className="col-span-2"><Label>Appointment Window</Label><Input value={form.appointment_window} onChange={(e) => setForm({ ...form, appointment_window: e.target.value })} placeholder="e.g. Thu 2026-10-10 09:00–11:00" disabled={!editable || !form.appointment_required} className="rounded-sm" data-testid="bol-appointment-window" /></div>
          <div className="col-span-2 md:col-span-4"><Label>Special Instructions (shows on print)</Label><Textarea rows={2} value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="bol-special-instructions" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Shipper (From)</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Ship From Name</Label><Input value={form.ship_from_name} onChange={(e) => setForm({ ...form, ship_from_name: e.target.value })} placeholder="Defaults to company name" disabled={!editable} className="rounded-sm" data-testid="bol-ship-from-name" /></div>
          <div><Label>Ship From Contact</Label><Input value={form.ship_from_contact} onChange={(e) => setForm({ ...form, ship_from_contact: e.target.value })} placeholder="Dock contact person" disabled={!editable} className="rounded-sm" data-testid="bol-ship-from-contact" /></div>
          <div className="col-span-2 md:col-span-3"><Label>Ship From Address</Label><Textarea rows={2} value={form.ship_from_address} onChange={(e) => setForm({ ...form, ship_from_address: e.target.value })} placeholder="Overrides default facility address on the printed BoL" disabled={!editable} className="rounded-sm" data-testid="bol-ship-from-address" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Carrier</CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Carrier</Label>
            <CarrierSelect
              value={form.carrier}
              onChange={(v) => setForm({ ...form, carrier: v })}
              onPickRich={(c) => setForm(prev => ({
                ...prev,
                carrier: c.name,
                carrier_scac: c.scac || prev.carrier_scac,
                freight_terms: c.default_freight_terms || prev.freight_terms,
              }))}
              disabled={!editable}
              testid="bol-carrier"
            />
          </div>
          <div><Label>SCAC Code</Label><Input value={form.carrier_scac} onChange={(e) => setForm({ ...form, carrier_scac: e.target.value })} placeholder="4-letter" disabled={!editable} className="rounded-sm mono" data-testid="bol-scac" /></div>
          <div><Label>Tracking / PRO #</Label><Input value={form.tracking_or_pro_number} onChange={(e) => setForm({ ...form, tracking_or_pro_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="bol-pro-number" /></div>
          <div><Label>Trailer #</Label><Input value={form.trailer_number} onChange={(e) => setForm({ ...form, trailer_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="bol-trailer" /></div>
          <div className="col-span-2"><Label>Driver Name</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="bol-driver" /></div>
        </CardContent>
      </Card>

      {/* iter91 — Shipment cost from carrier invoice (feeds rate-negotiation analytics) */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Shipment Cost <span className="text-xs font-normal text-slate-500 ml-2">(from carrier invoice — powers rate-negotiation analytics)</span></CardTitle></CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Freight Cost ($)</Label><Input type="number" min="0" step="0.01" value={form.freight_cost} onChange={(e) => setForm({ ...form, freight_cost: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="bol-freight-cost" /></div>
          <div><Label>Fuel Surcharge ($)</Label><Input type="number" min="0" step="0.01" value={form.fuel_surcharge} onChange={(e) => setForm({ ...form, fuel_surcharge: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="bol-fuel-surcharge" /></div>
          <div><Label>Accessorial ($)</Label><Input type="number" min="0" step="0.01" value={form.accessorial_charges} onChange={(e) => setForm({ ...form, accessorial_charges: e.target.value })} disabled={!editable} className="rounded-sm mono text-right" data-testid="bol-accessorial" /></div>
          <div><Label className="text-slate-500">Total (auto)</Label><div className="h-9 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-end px-3 mono font-bold text-slate-900" data-testid="bol-total-cost">${(Number(form.freight_cost || 0) + Number(form.fuel_surcharge || 0) + Number(form.accessorial_charges || 0)).toFixed(2)}</div></div>
          <div className="col-span-2 md:col-span-4"><Label>Cost Notes (invoice #, PRO #, dispute reason, etc.)</Label><Input value={form.cost_notes} onChange={(e) => setForm({ ...form, cost_notes: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="bol-cost-notes" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Freight Lines</CardTitle>
          {editable && <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm" data-testid="bol-add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Line</Button>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Description *</TableHead>
              <TableHead className="text-right">Pieces</TableHead>
              <TableHead className="text-right">Weight (lbs)</TableHead>
              <TableHead>Freight Class</TableHead>
              <TableHead>NMFC</TableHead>
              <TableHead>Hazmat</TableHead>
              {editable && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {form.lines.map((ln, i) => (
                <TableRow key={ln.id} data-testid={`bol-line-${i}`}>
                  <TableCell className="min-w-[240px]"><Input value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} disabled={!editable} className="rounded-sm text-xs" data-testid={`bol-line-desc-${i}`} /></TableCell>
                  <TableCell className="text-right w-20"><Input type="number" min="0" value={ln.piece_count} onChange={(e) => updateLine(i, { piece_count: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`bol-line-pieces-${i}`} /></TableCell>
                  <TableCell className="text-right w-28"><Input type="number" min="0" step="0.01" value={ln.weight_lbs} onChange={(e) => updateLine(i, { weight_lbs: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`bol-line-weight-${i}`} /></TableCell>
                  <TableCell className="w-24"><Input value={ln.freight_class || ''} onChange={(e) => updateLine(i, { freight_class: e.target.value })} placeholder="e.g. 70" disabled={!editable} className="rounded-sm text-xs mono" /></TableCell>
                  <TableCell className="w-28"><Input value={ln.nmfc_code || ''} onChange={(e) => updateLine(i, { nmfc_code: e.target.value })} disabled={!editable} className="rounded-sm text-xs mono" /></TableCell>
                  <TableCell className="w-16 text-center"><Checkbox checked={!!ln.hazmat} onCheckedChange={(v) => updateLine(i, { hazmat: !!v })} disabled={!editable} data-testid={`bol-line-hazmat-${i}`} /></TableCell>
                  {editable && <TableCell className="w-10"><Button variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-600 h-7 w-7"><X className="w-3.5 h-3.5" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardContent className="p-4 flex items-end justify-between gap-6 flex-wrap">
          <div className="text-sm space-y-1" data-testid="bol-totals">
            <div>Total Pieces: <span className="mono font-semibold ml-2">{totalPieces}</span></div>
            <div>Total Weight: <span className="mono font-semibold ml-2">{totalWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs</span></div>
            {(Number(form.freight_cost || 0) + Number(form.fuel_surcharge || 0) + Number(form.accessorial_charges || 0)) > 0 && (
              <div>Shipment Cost: <span className="mono font-semibold ml-2">${(Number(form.freight_cost || 0) + Number(form.fuel_surcharge || 0) + Number(form.accessorial_charges || 0)).toFixed(2)}</span></div>
            )}
            {hasHaz && <div className="text-red-700 font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Hazmat shipment</div>}
          </div>
          {editable && (
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bol-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create Draft' : 'Save Changes')}
            </Button>
          )}
        </CardContent>
      </Card>

      {!isNew && (
        <EvidencePhotoGallery
          api={api}
          docType="bill_of_lading"
          docId={id}
          evidenceType="scale_ticket"
          title="Scale Ticket Photo"
          emptyHint="Take a photo of the crate on the scale — the image embeds on the printed BoL and PDF."
          singleShot={true}
          testIdPrefix="bol-scale"
          compact={true}
        />
      )}
    </div>
  );
};

export default BillOfLadingDetailPage;
